import React, { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { Icon } from "@iconify-icon/react";
import { useStore } from "../../../stores/StoreContext";
import LoadingSpinner from "../../../HelperComponents/LoadingSpinner";
import DetailedList from "../../../HelperComponents/DetailedList";
import { getSimpleDateFormat, formatBytes } from "../../../Utility/Utility";
import { RecycleBinItem } from "./Documents.types";

/**
 * RecycleBinPanel Component
 * Slide-out panel that displays soft-deleted documents from SharePoint recycle bin.
 * Allows authenticated users to restore their deleted files.
 */
const RecycleBinPanel: React.FC = observer(() => {
  const { documentStore, uiStore } = useStore();
  const isOpen = uiStore.isRecycleBinOpen;
  const [restoringItemId, setRestoringItemId] = useState<string | null>(null);

  // Load recycle bin items when panel is opened
  useEffect(() => {
    if (isOpen) {
      documentStore.fetchRecycleBinItems();
    }
  }, [isOpen, documentStore]);

  // Handle panel close
  const handleClose = () => {
    uiStore.closeRecycleBin();
    uiStore.clearError();
  };

  // Restore action handler
  const handleRestore = useCallback(async (item: RecycleBinItem) => {
    if (documentStore.isRestoring) return;

    try {
      setRestoringItemId(item.id);
      await documentStore.restoreRecycleBinItem(item.id, item.leafName);
    } finally {
      setRestoringItemId(null);
    }
  }, [documentStore]);

  // Column definitions for the recycle bin listing
  const columns = useMemo(
    () => [
      {
        key: "leafName",
        name: "Name",
        fieldName: "leafName",
        minWidth: 200,
        maxWidth: 260,
        isResizable: true,
        isSortable: true,
        headerClassName: "detailedListHeader",
        className: "detailedListColumn",
        onRender: (item: RecycleBinItem) => (
          <span style={{ fontWeight: 500, wordBreak: "break-all" }}>
            {item.leafName}
          </span>
        ),
      },
      {
        key: "deletedDate",
        name: "Deleted On",
        fieldName: "deletedDate",
        minWidth: 90,
        maxWidth: 110,
        isResizable: true,
        isSortable: true,
        headerClassName: "detailedListHeader",
        className: "detailedListColumn",
        onRender: (item: RecycleBinItem) => {
          if (!item.deletedDate) return <span></span>;
          return <span>{getSimpleDateFormat(item.deletedDate)}</span>;
        },
      },
      {
        key: "size",
        name: "Size",
        fieldName: "size",
        minWidth: 70,
        maxWidth: 90,
        isResizable: true,
        isSortable: true,
        headerClassName: "detailedListHeader",
        className: "detailedListColumn",
        onRender: (item: RecycleBinItem) => {
          if (item.size == null) return <span>-</span>;
          return <span>{formatBytes(item.size)}</span>;
        },
      },
      {
        key: "Actions",
        name: "",
        fieldName: "Actions",
        minWidth: 60,
        maxWidth: 60,
        isResizable: false,
        isSortable: false,
        headerClassName: "detailedListHeader",
        className: "detailedListColumn",
        onRender: (item: RecycleBinItem) => {
          const isThisItemRestoring = restoringItemId === item.id;
          return (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <button
                type="button"
                onClick={() => handleRestore(item)}
                disabled={documentStore.isRestoring}
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: documentStore.isRestoring ? "not-allowed" : "pointer",
                  padding: 6,
                  borderRadius: 9999,
                  color: "hsl(var(--primary))",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: documentStore.isRestoring ? 0.6 : 1,
                  transition: "background 0.15s ease",
                }}
                title="Restore Document"
              >
                {isThisItemRestoring ? (
                  <Icon
                    icon="mdi:loading"
                    width="18"
                    height="18"
                    className="animate-spin"
                  />
                ) : (
                  <Icon icon="mdi:restore" width="18" height="18" />
                )}
              </button>
            </div>
          );
        },
      },
    ],
    [restoringItemId, documentStore.isRestoring, handleRestore]
  );

  const items = documentStore.filteredRecycleBinItems;

  if (!isOpen) {
    return null;
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.4)",
        display: "flex",
        justifyContent: "flex-end",
        zIndex: 1000,
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        style={{
          width: 580,
          maxWidth: "95%",
          height: "100%",
          backgroundColor: "hsl(var(--background))",
          color: "hsl(var(--foreground))",
          boxShadow: "-4px 0 16px rgba(0,0,0,0.2)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 16px",
            borderBottom: "1px solid var(--app-border-color)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Icon icon="mdi:trash-can-outline" width="20" height="20" />
            <h2
              style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              Recycle Bin
            </h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            style={{
              border: "none",
              background: "transparent",
              color: "hsl(var(--foreground))",
              cursor: "pointer",
              fontSize: 22,
              lineHeight: 1,
              padding: 4,
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            padding: "16px",
            overflowY: "auto",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}
        >
          {/* Error alerts inside panel */}
          {uiStore.error && (
            <div
              style={{
                marginBottom: 16,
                padding: "8px 10px",
                borderRadius: 8,
                border: "1px solid hsl(var(--destructive))",
                backgroundColor: "hsla(var(--destructive),0.08)",
                fontSize: 13,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>{uiStore.error}</span>
              <button
                type="button"
                onClick={() => uiStore.clearError()}
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  color: "hsl(var(--destructive))",
                  fontSize: 16,
                  fontWeight: "bold",
                }}
              >
                ×
              </button>
            </div>
          )}

          {/* Retention Warning Notice */}
          <div
            style={{
              marginBottom: 16,
              padding: "10px 12px",
              borderRadius: 8,
              border: "1px solid hsla(205, 100%, 60%, 0.25)",
              backgroundColor: "hsla(205, 100%, 60%, 0.08)",
              fontSize: 12,
              lineHeight: "1.4",
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              color: "hsl(var(--foreground))",
            }}
          >
            <Icon
              icon="mdi:information-outline"
              width="16"
              height="16"
              style={{ marginTop: 2, flexShrink: 0, color: "hsl(var(--primary))" }}
            />
            <div>
              <strong>Note:</strong> It is not guaranteed that files deleted more than a week ago will remain available here. We strongly recommend restoring any deleted document as soon as you realize it is needed.
            </div>
          </div>

          {/* Loader */}
          {documentStore.isRecycleBinLoading ? (
            <div
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <LoadingSpinner />
            </div>
          ) : items.length > 0 ? (
            <div style={{ flex: 1, overflowX: "auto" }}>
              <DetailedList
                items={items}
                columns={columns}
                listType="shimmered"
                emptyMessage="No recycled documents found."
                loading={false}
              />
            </div>
          ) : (
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                color: "hsl(var(--muted-foreground))",
                textAlign: "center",
                gap: 8,
              }}
            >
              <Icon icon="mdi:trash-can-empty" width="48" height="48" />
              <p style={{ margin: 0, fontSize: 14, fontWeight: 500 }}>
                Your Recycle Bin is empty.
              </p>
              <p style={{ margin: 0, fontSize: 12, opacity: 0.8 }}>
                Soft-deleted documents will appear here for recovery.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default RecycleBinPanel;
